import type { FetcherArgs, FetcherWithKey } from '@ricoh/ria.client.core/plugins/swr/fetcher';
import type { Middleware } from 'swr';

import { createLogger, loggerStyles } from '@ricoh/ria.client.core/plugins/app/logger';
import { isType } from '@ricoh/ria.client.core/utils/data/object';

import type { FetcherLogData } from './_logger.type';

const logger = createLogger('swr');

/**
 * Create FetcherLogData.
 *
 * @template TRequestData Request data.
 * @param {FetcherArgs} args Fetcher arguments.
 *
 * @returns {FetcherLogData<TRequestData>} FetcherLogData.
 */
function createLogData<TRequestData = never>(...args: FetcherArgs<TRequestData>): FetcherLogData<TRequestData> {
  const [key, options] = args;

  if (isType<FetcherWithKey>(key, 'fetcherWithKey')) {
    return {
      key: JSON.stringify(key.key ?? ''),
      request: key.arg ?? {},
    };
  }

  if (typeof key === 'string') {
    return {
      key,
      request: options?.arg ?? {},
    };
  }

  return {
    key: JSON.stringify(key ?? ''),
    request: options?.arg ?? {},
  };
}

/**
 * Create Logger middleware.
 *
 * @returns {Middleware} Logger middleware.
 */
export function createLoggerMiddleware(): Middleware {
  return (useSWRNext) => (key, fetch, options) => {
    if (!fetch) {
      return useSWRNext(key, fetch, options);
    }

    const fetchWithLogger = async (...args: FetcherArgs) => {
      const logData = createLogData(...args);

      logger.writeWithData('info', `${loggerStyles.requestText('Request')} fetch ${loggerStyles.keyText(logData.key)}`, logData.request);

      try {
        const response = await fetch(...args);
        logger.writeWithData('info', `${loggerStyles.responseText('Response')} fetch ${loggerStyles.keyText(logData.key)}`, response);

        return response;
      } catch (exception) {
        logger.writeWithData('error', `${loggerStyles.errorText('Error')} fetch ${loggerStyles.keyText(logData.key)}`, exception);

        throw exception;
      }
    };

    return useSWRNext(key, fetchWithLogger, options);
  };
}
